import type { KickoffAck, KickoffBereich } from '../../services/kickoff/kickoffTypes';

/**
 * Statusanzeigen des Projekt-Kickoffs (docs/projekt-kickoff-plan.md §4.4):
 * Hub-Stammdaten („Kalender bestätigt am …") und Kennzeichen in der
 * Admin-Projektliste („Kalender offen · 3 T.").
 *
 * Rein und ohne DOM, damit die Texte und die Tageszählung ohne jsdom
 * testbar sind. Die Komponenten in KickoffStatus.tsx zeigen nur an.
 */

/**
 * Ab so vielen offenen Kalendertagen wird das Kennzeichen rot. Bewusst
 * Kalendertage, nicht Arbeitstage: die Liste soll früh warnen — die
 * Eskalation per Mail (Stufe 2) zählt Arbeitstage und kommt danach.
 */
export const KICKOFF_ROT_AB_TAGEN = 2;

const BEREICH_TEXT: Record<KickoffBereich, string | null> = {
    alle: null,
    planung: 'Planung',
    ausfuehrung: 'Ausführung',
};

/** Reihenfolge der Zeilen im Hub: der Besitzer-Normalfall zuerst. */
const BEREICH_RANG: Record<KickoffBereich, number> = { alle: 0, ausfuehrung: 1, planung: 2 };

const zeitpunkt = (iso: string | null | undefined): Date | null => {
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
};

/**
 * Ganze Kalendertage zwischen `iso` und `jetzt`, in Ortszeit gezählt: eine
 * Zeile von gestern 23:00 ist heute früh „seit 1 Tag" offen, nicht „seit
 * 0 Tagen". UTC-Mitternacht auf den Ortsdatumsteilen umgeht Sommer-/Winterzeit
 * (ein Tag mit 23 oder 25 Stunden zählt trotzdem als einer).
 * Ungültiges Datum → null; Zeitpunkt in der Zukunft (Uhrabweichung) → 0.
 */
export const kickoffTageSeit = (iso: string | null | undefined, jetzt: Date = new Date()): number | null => {
    const d = zeitpunkt(iso);
    if (!d) return null;
    const tag = (x: Date) => Date.UTC(x.getFullYear(), x.getMonth(), x.getDate());
    return Math.max(0, Math.round((tag(jetzt) - tag(d)) / 86_400_000));
};

/** `TT.MM.JJJJ` in Ortszeit; ungültig → ''. */
export const kickoffDatum = (iso: string | null | undefined): string => {
    const d = zeitpunkt(iso);
    if (!d) return '';
    const zwei = (n: number) => String(n).padStart(2, '0');
    return `${zwei(d.getDate())}.${zwei(d.getMonth() + 1)}.${d.getFullYear()}`;
};

const seitText = (tage: number | null): string => {
    if (tage === null) return '';
    if (tage === 0) return ' (seit heute)';
    return tage === 1 ? ' (seit 1 Tag)' : ` (seit ${tage} Tagen)`;
};

/**
 * Je Empfänger und Bereich nur die JÜNGSTE Zeile. Eine bestätigte alte Zeile
 * und eine neue offene (Stufe 2: Terminänderung, Besitzerwechsel) dürfen
 * nebeneinander stehen — maßgeblich ist die neue, sonst zeigte die Liste
 * „bestätigt", obwohl die geänderten Termine noch offen sind.
 */
export const aktuelleKickoffs = (acks: ReadonlyArray<KickoffAck>): KickoffAck[] => {
    const neueste = new Map<string, KickoffAck>();
    for (const ack of acks) {
        const schluessel = `${ack.project_id}|${ack.recipient_id}|${ack.bereich}`;
        const bisher = neueste.get(schluessel);
        if (!bisher || (zeitpunkt(ack.created_at)?.getTime() ?? 0) > (zeitpunkt(bisher.created_at)?.getTime() ?? 0)) {
            neueste.set(schluessel, ack);
        }
    }
    return [...neueste.values()];
};

export interface KickoffStatusZeile {
    id: string;
    recipientId: string;
    /** 'Planung' / 'Ausführung'; null bei 'alle' (dann ohne Bereichsangabe). */
    bereichText: string | null;
    bestaetigt: boolean;
    /** „Kalender bestätigt am 12.10.2026" bzw. „Kalender noch nicht bestätigt (seit 3 Tagen)". */
    statusText: string;
    /** Offene Kalendertage; null bei bestätigt oder unlesbarem Datum. */
    offenSeitTagen: number | null;
    /** Rot zeigen: offen seit mindestens KICKOFF_ROT_AB_TAGEN. */
    ueberfaellig: boolean;
    /** „Mail nicht zugestellt: …" oder null. */
    mailFehlerText: string | null;
}

export const kickoffStatusZeile = (ack: KickoffAck, jetzt: Date = new Date()): KickoffStatusZeile => {
    const bestaetigt = !!ack.bestaetigt_am;
    const offenSeitTagen = bestaetigt ? null : kickoffTageSeit(ack.created_at, jetzt);
    const datum = kickoffDatum(ack.bestaetigt_am);
    const fehler = (ack.mail_fehler || '').trim();
    return {
        id: ack.id,
        recipientId: ack.recipient_id,
        bereichText: BEREICH_TEXT[ack.bereich] ?? null,
        bestaetigt,
        statusText: bestaetigt
            ? (datum ? `Kalender bestätigt am ${datum}` : 'Kalender bestätigt')
            : `Kalender noch nicht bestätigt${seitText(offenSeitTagen)}`,
        offenSeitTagen,
        ueberfaellig: offenSeitTagen !== null && offenSeitTagen >= KICKOFF_ROT_AB_TAGEN,
        // Eine bestätigte Zeile braucht keine Mailwarnung mehr: die Termine
        // stehen im Kalender, auf welchem Weg auch immer.
        mailFehlerText: !bestaetigt && fehler ? `Mail nicht zugestellt: ${fehler}` : null,
    };
};

/** Zeilen für den Hub: nur die jüngste je Empfänger/Bereich, Normalfall zuerst. */
export const kickoffStatusZeilen = (acks: ReadonlyArray<KickoffAck>, jetzt: Date = new Date()): KickoffStatusZeile[] =>
    aktuelleKickoffs(acks)
        .sort((a, b) =>
            (BEREICH_RANG[a.bereich] ?? 9) - (BEREICH_RANG[b.bereich] ?? 9)
            || (zeitpunkt(a.created_at)?.getTime() ?? 0) - (zeitpunkt(b.created_at)?.getTime() ?? 0))
        .map(ack => kickoffStatusZeile(ack, jetzt));

export interface KickoffKennzeichen {
    /** „Kalender ✔" oder „Kalender offen · 3 T.". */
    text: string;
    bestaetigt: boolean;
    rot: boolean;
    /** Tooltip mit der ausführlichen Lage. */
    titel: string;
}

/**
 * Kennzeichen für EIN Projekt in der Admin-Liste. Ohne Zeilen → null (kein
 * Kennzeichen: Altprojekt, Übungsprojekt, Migration fehlt — fail-open).
 * Ein Projekt gilt erst als bestätigt, wenn ALLE Zuständigen bestätigt haben;
 * gezählt wird die am längsten offene Zeile.
 */
export const kickoffKennzeichen = (acks: ReadonlyArray<KickoffAck>, jetzt: Date = new Date()): KickoffKennzeichen | null => {
    const zeilen = aktuelleKickoffs(acks).map(a => kickoffStatusZeile(a, jetzt));
    if (zeilen.length === 0) return null;
    const offen = zeilen.filter(z => !z.bestaetigt);
    if (offen.length === 0) {
        return {
            text: 'Kalender ✔',
            bestaetigt: true,
            rot: false,
            titel: zeilen.length === 1
                ? zeilen[0].statusText
                : `Alle ${zeilen.length} Zuständigen haben die Termine im Kalender bestätigt.`,
        };
    }
    const tage = offen.reduce<number | null>(
        (max, z) => (z.offenSeitTagen === null ? max : Math.max(max ?? 0, z.offenSeitTagen)),
        null,
    );
    const mailFehler = offen.some(z => z.mailFehlerText);
    const titelTeile = [
        zeilen.length === 1
            ? offen[0].statusText
            : `Termine bestätigt: ${zeilen.length - offen.length} von ${zeilen.length} Zuständigen.`,
        ...(mailFehler ? ['Mindestens eine Kickoff-Mail wurde nicht zugestellt.'] : []),
    ];
    return {
        text: tage === null ? 'Kalender offen' : `Kalender offen · ${tage} T.`,
        bestaetigt: false,
        rot: tage !== null && tage >= KICKOFF_ROT_AB_TAGEN,
        titel: titelTeile.join(' '),
    };
};

/** Zeilen nach Projekt gruppieren — für EINEN Abruf über die ganze Listenseite. */
export const kickoffsNachProjekt = (acks: ReadonlyArray<KickoffAck>): Map<string, KickoffAck[]> => {
    const map = new Map<string, KickoffAck[]>();
    for (const ack of acks) {
        const liste = map.get(ack.project_id);
        if (liste) liste.push(ack);
        else map.set(ack.project_id, [ack]);
    }
    return map;
};
